/* ==========================================================================
   Guided tour — behaviour layer
   Avanti Design Rules / methods/guided-tour

   Dependency-free, no build step. Pairs with tour.css.

     const tour = new Tour({
       storageKey: 'dealroom-tour-v1',
       steps: [
         { target: null, title: '…', body: '…' },
         { target: '[data-tour="scrubber"]', title: '…', body: '…',
           placement: 'top', before: async () => app.setWeek(7) },
       ],
     });
     tour.startIfUnseen();          // first visit only
     tour.start();                  // replay, from a help menu or "?"
     Tour.reset('dealroom-tour-v1') // forget that it was seen
   ========================================================================== */

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), ' +
  'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

const GAP = 18;      // space between target edge and card edge (caret lives here)
const MARGIN = 16;   // minimum distance from the viewport edge
const CARET_HALF = 11;
const CARET_INSET = 20; // keep the caret this far from the card's corners

const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isPhone = () => window.innerWidth <= 640;

/* rAF is paused in a background tab, so every wait races it against a timer.
   Without this a tour started while the tab is hidden never positions itself. */
const nextFrame = () =>
  new Promise((resolve) => {
    let done = false;
    const fin = () => { if (!done) { done = true; resolve(); } };
    requestAnimationFrame(fin);
    setTimeout(fin, 50);
  });

const raf2 = async () => { await nextFrame(); await nextFrame(); };

/** Wait until an element's rect stops changing (smooth scroll has settled). */
function fullyVisible(el) {
  const r = el.getBoundingClientRect();
  return r.top >= 0 && r.left >= 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth;
}

function waitForScrollSettle(el, timeout = 600) {
  if (!el || reducedMotion()) return raf2();
  return new Promise((resolve) => {
    const started = Date.now();
    let last = null;
    let stable = 0;
    const tick = () => {
      const r = el.getBoundingClientRect();
      const key = `${Math.round(r.top)}:${Math.round(r.left)}`;
      stable = key === last ? stable + 1 : 0;
      last = key;
      if (stable >= 2 || Date.now() - started > timeout) resolve();
      else setTimeout(tick, 16);   // timer, not rAF — survives a hidden tab
    };
    setTimeout(tick, 16);
  });
}

function readRadius(el, pad) {
  try {
    const r = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
    return Math.min(r + pad, 24);
  } catch {
    return 10;
  }
}

const store = {
  get(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key, val) {
    try { localStorage.setItem(key, val); } catch { /* private mode */ }
  },
  remove(key) {
    try { localStorage.removeItem(key); } catch { /* private mode */ }
  },
};

class Tour {
  /**
   * @param {object}   opts
   * @param {Array}    opts.steps           [{ target, title, body, placement,
   *                                          before, after, spotlightPadding }]
   * @param {string}  [opts.storageKey]     localStorage key for "already seen"
   * @param {string}  [opts.scrimClick]     'block' | 'next' | 'skip'
   * @param {object}  [opts.labels]         { skip, back, next, done, step }
   * @param {Function}[opts.onFinish]
   * @param {Function}[opts.onSkip]
   */
  constructor(opts = {}) {
    this.steps = opts.steps || [];
    this.storageKey = opts.storageKey || null;
    this.scrimClick = opts.scrimClick || 'block';
    // A tour normally focuses its primary action so Enter always advances.
    // A host that renders the page as a decorative preview (a thumbnail in an
    // iframe) must be able to switch that off: nothing there was a deliberate
    // user gesture, and grabbing focus would steal it from the real page.
    this.focusPrimary = opts.focusPrimary !== false;
    this.onFinish = opts.onFinish || (() => {});
    this.onSkip = opts.onSkip || (() => {});
    this.labels = Object.assign(
      {
        skip: 'Tour überspringen',
        back: 'Zurück',
        next: 'Weiter',
        done: 'Fertig',
        step: (i, n) => `Schritt ${i}/${n}`,
        srStep: (i, n) => `Schritt ${i} von ${n}`,
      },
      opts.labels
    );

    this.index = -1;
    this.open = false;
    this.returnFocusTo = null;
    this.currentTarget = null;
    this._frame = null;

    this._build();
  }

  /* ---------------------------------------------------------------- DOM -- */

  _build() {
    const root = document.createElement('div');
    root.className = 'tour-root';
    root.setAttribute('data-tour-open', 'false');
    root.hidden = true;

    // Four scrim rects, so the spotlit element stays genuinely interactive.
    this.scrims = ['top', 'right', 'bottom', 'left'].map((side) => {
      const el = document.createElement('div');
      el.className = 'tour-scrim';
      el.dataset.tourSide = side;
      el.setAttribute('aria-hidden', 'true');
      el.addEventListener('click', () => this._onScrimClick());
      root.appendChild(el);
      return el;
    });

    this.ring = document.createElement('div');
    this.ring.className = 'tour-ring';
    this.ring.setAttribute('aria-hidden', 'true');
    root.appendChild(this.ring);

    const card = document.createElement('div');
    card.className = 'tour-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('tabindex', '-1');
    card.innerHTML = `
      <span class="tour-caret" aria-hidden="true"></span>
      <p class="tour-eyebrow" aria-hidden="true"></p>
      <h2 class="tour-title"></h2>
      <div class="tour-body"></div>
      <ul class="tour-dots" aria-hidden="true"></ul>
      <div class="tour-footer">
        <button type="button" class="tour-btn tour-skip"></button>
        <div class="tour-actions">
          <button type="button" class="tour-btn tour-back"></button>
          <button type="button" class="tour-btn tour-next"></button>
        </div>
      </div>
      <div class="tour-sr" role="status" aria-live="polite"></div>
    `;
    root.appendChild(card);

    const q = (sel) => card.querySelector(sel);
    this.root = root;
    this.card = card;
    this.caret = q('.tour-caret');
    this.eyebrow = q('.tour-eyebrow');
    this.title = q('.tour-title');
    this.bodyEl = q('.tour-body');
    this.dots = q('.tour-dots');
    this.skipBtn = q('.tour-skip');
    this.backBtn = q('.tour-back');
    this.nextBtn = q('.tour-next');
    this.srEl = q('.tour-sr');

    const uid = Math.random().toString(36).slice(2, 8);
    this.title.id = `tour-title-${uid}`;
    this.bodyEl.id = `tour-body-${uid}`;
    card.setAttribute('aria-labelledby', this.title.id);
    card.setAttribute('aria-describedby', this.bodyEl.id);

    this.skipBtn.textContent = this.labels.skip;
    this.backBtn.textContent = this.labels.back;

    this.skipBtn.addEventListener('click', () => this.skip());
    this.backBtn.addEventListener('click', () => this.back());
    this.nextBtn.addEventListener('click', () => this.next());

    this._onKeydown = this._onKeydown.bind(this);
    this._schedulePosition = this._schedulePosition.bind(this);

    document.body.appendChild(root);
  }

  /* ------------------------------------------------------------ lifecycle */

  get seen() {
    return this.storageKey ? store.get(this.storageKey) === 'done' : false;
  }

  startIfUnseen() {
    if (!this.seen) this.start();
    return this;
  }

  start(at = 0) {
    if (this.open || !this.steps.length) return this;
    this.open = true;
    this.returnFocusTo = document.activeElement;
    this.root.hidden = false;
    // Force a reflow so the browser has a "closed" frame to transition from.
    // A rAF here would never fire in a background tab and the tour would
    // silently stay invisible.
    void this.root.offsetHeight;
    this.root.setAttribute('data-tour-open', 'true');

    document.addEventListener('keydown', this._onKeydown, true);
    window.addEventListener('scroll', this._schedulePosition, { passive: true, capture: true });
    window.addEventListener('resize', this._schedulePosition, { passive: true });
    document.addEventListener('visibilitychange', this._schedulePosition);

    this.index = -1;
    this._goTo(at);
    return this;
  }

  next() {
    if (this.index >= this.steps.length - 1) this.finish();
    else this._goTo(this.index + 1);
  }

  back() {
    if (this.index > 0) this._goTo(this.index - 1);
  }

  skip() {
    this._end();
    this.onSkip(this.index);
  }

  finish() {
    this._end();
    this.onFinish();
  }

  _end() {
    if (!this.open) return;
    const step = this.steps[this.index];
    if (step && typeof step.after === 'function') {
      try { step.after(); } catch (e) { console.error('[tour] after hook', e); }
    }

    this.open = false;
    this.root.setAttribute('data-tour-open', 'false');
    document.removeEventListener('keydown', this._onKeydown, true);
    window.removeEventListener('scroll', this._schedulePosition, { capture: true });
    window.removeEventListener('resize', this._schedulePosition);
    document.removeEventListener('visibilitychange', this._schedulePosition);

    // Skipping counts as a decision — do not re-show it later.
    if (this.storageKey) store.set(this.storageKey, 'done');

    const hide = () => { this.root.hidden = true; };
    reducedMotion() ? hide() : setTimeout(hide, 220);

    if (this.returnFocusTo && this.returnFocusTo.focus) {
      this.returnFocusTo.focus({ preventScroll: true });
    }
    this.currentTarget = null;
  }

  destroy() {
    this._end();
    this.root.remove();
  }

  static reset(storageKey) {
    store.remove(storageKey);
  }

  static isSeen(storageKey) {
    return store.get(storageKey) === 'done';
  }

  /* ----------------------------------------------------------- rendering  */

  async _goTo(index) {
    const prev = this.steps[this.index];
    if (prev && typeof prev.after === 'function') {
      try { prev.after(); } catch (e) { console.error('[tour] after hook', e); }
    }

    this.index = index;
    const step = this.steps[index];
    if (!step) return this.finish();

    // The step performs itself before we measure anything (principle 3).
    if (typeof step.before === 'function') {
      try { await step.before(); } catch (e) { console.error('[tour] before hook', e); }
    }
    if (!this.open || this.index !== index) return; // tour moved on while awaiting

    this._renderContent(step, index);

    const target = this._resolveTarget(step);
    this.currentTarget = target;

    // Only scroll when the target is not already fully visible: centring a target that sits in a
    // sticky or fixed panel would scroll the page underneath for nothing.
    if (target && !fullyVisible(target)) {
      target.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
      await waitForScrollSettle(target);
      if (!this.open || this.index !== index) return;
    }

    this._position();

    // Rule: the primary action is focused, so Enter always advances.
    if (!this.focusPrimary) return;
    await raf2();
    if (this.open && this.index === index) {
      this.nextBtn.focus({ preventScroll: true });
    }
  }

  _resolveTarget(step) {
    if (!step.target) return null;
    const el =
      typeof step.target === 'string'
        ? document.querySelector(step.target)
        : step.target;
    if (!el || !el.getBoundingClientRect) {
      // Principle 9: degrade to a centred, anchorless step. Never throw.
      console.warn('[tour] target not found, centring step:', step.target);
      return null;
    }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      console.warn('[tour] target has no box, centring step:', step.target);
      return null;
    }
    return el;
  }

  _renderContent(step, index) {
    const n = this.steps.length;
    this.eyebrow.textContent = this.labels.step(index + 1, n);
    this.title.textContent = step.title || '';
    this.bodyEl.textContent = step.body || '';

    if (this.dots.children.length !== n) {
      this.dots.innerHTML = '';
      for (let i = 0; i < n; i += 1) {
        const li = document.createElement('li');
        li.className = 'tour-dot';
        this.dots.appendChild(li);
      }
    }
    Array.from(this.dots.children).forEach((dot, i) => {
      dot.setAttribute('data-tour-active', String(i === index));
    });

    this.backBtn.disabled = index === 0;
    this.nextBtn.textContent =
      index === n - 1 ? this.labels.done : this.labels.next;

    // Principle 5, for screen readers too.
    this.srEl.textContent =
      `${this.labels.srStep(index + 1, n)}. ${step.title || ''}`;
  }

  /* ---------------------------------------------------------- positioning */

  _schedulePosition() {
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      if (this.open) this._position();
    });
  }

  _position() {
    const step = this.steps[this.index];
    if (!step) return;
    const target = this.currentTarget;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    if (!target) {
      this._paintScrim(null, vw, vh);
      this.ring.setAttribute('data-tour-anchored', 'false');
      this.card.setAttribute('data-tour-placement', 'center');
      const cw = this.card.offsetWidth;
      const ch = this.card.offsetHeight;
      this.card.style.left = `${Math.max(MARGIN, (vw - cw) / 2)}px`;
      this.card.style.top = `${Math.max(MARGIN, (vh - ch) / 2)}px`;
      return;
    }

    const pad = step.spotlightPadding ?? 6;
    const r = target.getBoundingClientRect();
    const hole = {
      x: r.left - pad,
      y: r.top - pad,
      w: r.width + pad * 2,
      h: r.height + pad * 2,
    };

    this._paintScrim(hole, vw, vh);

    this.ring.setAttribute('data-tour-anchored', 'true');
    this.ring.style.left = `${hole.x}px`;
    this.ring.style.top = `${hole.y}px`;
    this.ring.style.width = `${hole.w}px`;
    this.ring.style.height = `${hole.h}px`;
    this.ring.style.borderRadius = `${readRadius(target, pad)}px`;

    if (isPhone()) {
      // Docked as a bottom sheet by CSS; nothing to compute, no caret.
      this.card.setAttribute('data-tour-placement', 'sheet');
      this.card.style.top = '';
      this.card.style.left = '';
      return;
    }

    const cw = this.card.offsetWidth;
    const ch = this.card.offsetHeight;
    const placement = this._choosePlacement(step.placement, hole, cw, ch, vw, vh);
    this.card.setAttribute('data-tour-placement', placement);

    let top;
    let left;
    if (placement === 'bottom' || placement === 'top') {
      top = placement === 'bottom' ? hole.y + hole.h + GAP : hole.y - GAP - ch;
      left = hole.x + hole.w / 2 - cw / 2;
      left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - cw - MARGIN));
      const caretX = hole.x + hole.w / 2 - left;
      this.caret.style.left = `${this._clampCaret(caretX, cw)}px`;
      this.caret.style.top = '';
    } else {
      left = placement === 'right' ? hole.x + hole.w + GAP : hole.x - GAP - cw;
      top = hole.y + hole.h / 2 - ch / 2;
      top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - ch - MARGIN));
      const caretY = hole.y + hole.h / 2 - top;
      this.caret.style.top = `${this._clampCaret(caretY, ch)}px`;
      this.caret.style.left = '';
    }

    top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - ch - MARGIN));
    this.card.style.top = `${top}px`;
    this.card.style.left = `${left}px`;
  }

  _clampCaret(centre, size) {
    const c = Math.min(Math.max(centre, CARET_INSET), Math.max(CARET_INSET, size - CARET_INSET));
    return c - CARET_HALF;
  }

  _choosePlacement(preferred, hole, cw, ch, vw, vh) {
    const fits = {
      bottom: hole.y + hole.h + GAP + ch <= vh - MARGIN,
      top: hole.y - GAP - ch >= MARGIN,
      right: hole.x + hole.w + GAP + cw <= vw - MARGIN,
      left: hole.x - GAP - cw >= MARGIN,
    };
    const opposite = { bottom: 'top', top: 'bottom', left: 'right', right: 'left' };
    const first = preferred && preferred !== 'auto' ? preferred : 'bottom';
    const order = [first, opposite[first], 'bottom', 'top', 'right', 'left'];
    return order.find((p) => p && fits[p]) || first;
  }

  _paintScrim(hole, vw, vh) {
    const [top, right, bottom, left] = this.scrims;
    const set = (el, x, y, w, h) => {
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${Math.max(0, w)}px`;
      el.style.height = `${Math.max(0, h)}px`;
    };
    if (!hole) {
      set(top, 0, 0, vw, vh);
      [right, bottom, left].forEach((el) => set(el, 0, 0, 0, 0));
      return;
    }
    set(top, 0, 0, vw, hole.y);
    set(bottom, 0, hole.y + hole.h, vw, vh - (hole.y + hole.h));
    set(left, 0, hole.y, hole.x, hole.h);
    set(right, hole.x + hole.w, hole.y, vw - (hole.x + hole.w), hole.h);
  }

  _onScrimClick() {
    if (this.scrimClick === 'next') this.next();
    else if (this.scrimClick === 'skip') this.skip();
  }

  /* ------------------------------------------------------------- keyboard */

  _onKeydown(e) {
    if (!this.open) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.skip();
      return;
    }

    if (e.key === 'Tab') {
      const items = Array.from(this.card.querySelectorAll(FOCUSABLE))
        .filter((el) => !el.disabled && el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }

    // Enter/Space resolve here rather than relying on native button
    // activation, so the primary action fires deterministically. preventDefault
    // also stops Space from scrolling the page underneath.
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      const active = document.activeElement;
      e.preventDefault();
      if (active === this.backBtn) this.back();
      else if (active === this.skipBtn) this.skip();
      else this.next();
      return;
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.back();
    }
  }
}

/* Load as a classic script (`<script src="tour.js"></script>`) and use the
   global, or convert to a module by appending `export default Tour;` and
   loading it with `<script type="module">`. */
if (typeof window !== 'undefined') window.Tour = Tour;

export { Tour };
